import { PhoneOutlined, WhatsAppOutlined } from '@ant-design/icons'
import { Button, Dropdown } from 'antd'
import axios from 'axios'
import React from 'react'
import WhatsAppButton from '../../components/dashboard/appointments/Calendar/WhatsAppButton'
import DOMPurify from 'dompurify'
import moment from 'moment'
import { gql } from '@apollo/client'
import { isMedicolize } from '../../variables'

export const toEnglishNumbers = str => {
  return str.replace(/[٠١٢٣٤٥٦٧٨٩]/g, d => d.charCodeAt(0) - 1632).replace(/[۰۱۲۳۴۵۶۷۸۹]/g, d => d.charCodeAt(0) - 1776)
}

// How a conversation is addressed on screen: by number, or by WhatsApp username when the contact hides their number
export const getConversationHandle = ({ phone, username, bsuid } = {}) =>
  phone ? `+${phone}` : username ? `@${username}` : bsuid || ''

export const getWhatsAppLink = (phone, text) => {
  if (!phone) return

  return `https://api.whatsapp.com/send?phone=${encodeURIComponent(
    phone
      .replace('_', '')
      .replace(/ /g, '')
      .replace(/[\u200E\u202A\u202C]/g, '')
      .replace(/[^\d+]/g, '')
  )}${text ? `&text=${encodeURIComponent(text.replace(/#/g, ''))}` : ''}&type=phone_number`
}

export const uploadFileAndGetSignedUrl = async (signS3, file, fileName, setProgress, token) => {
  const fileType = file.type === 'audio/mp3' ? 'audio/mpeg' : file.type

  const signedUrl = await signS3({
    variables: {
      fileSize: file.size,
      token,
      fileName:
        file.name ||
        fileName ||
        (file.type && file.type.split('/')[1]
          ? `${Math.random()
              .toString(36)
              .substring(2, 20)}.${file.type.split('/')[1]}`
          : Math.random()
              .toString(36)
              .substring(2, 20)),
      fileType
    }
  })

  const postReq = JSON.parse(signedUrl.data.signS3.signedRequest)

  const formData = new FormData()
  Object.keys(postReq.fields).forEach(key => {
    formData.append(key, postReq.fields[key])
  })

  formData.append('Content-Type', fileType)
  formData.append('file', file)

  await axios.post(postReq.url, formData, {
    onUploadProgress: setProgress
      ? progressEvent => {
          setProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total))
        }
      : undefined
  })

  return signedUrl
}

export const uploadFileToGoogleDrive = async ({ file, setProgress, getDriveToken, fileName, id, client, mainFolder }) => {
  let folderId
  let folderName = mainFolder

  if (id && !mainFolder) {
    const patient = client.readFragment(
      {
        id: `Patient:${id}`,
        fragment: gql`
          fragment PatientData on Patient {
            id
            referenceId
            firstName
            lastName
          }
        `
      },
      true
    )

    if (patient) {
      folderName = `#${patient.referenceId} ${patient.firstName} ${patient.lastName}`
    }
  }

  const tokenExpire = sessionStorage.getItem('googleDriveExpire')
  let token = sessionStorage.getItem('googleDrive')

  if (!(tokenExpire && token && moment(tokenExpire).isAfter(moment()))) {
    const res = await getDriveToken()

    token = res.data.getGoogleDriveToken.token

    sessionStorage.setItem('googleDrive', token)
    sessionStorage.setItem('googleDriveExpire', moment(res.data.getGoogleDriveToken.expiry).toString())
  }

  if (!folderName) {
    folderName = isMedicolize ? 'Medicolize' : 'Dentolize'
  }

  // Step 1: Search for the folder
  const searchResponse = await axios.get('https://www.googleapis.com/drive/v3/files', {
    headers: {
      Authorization: `Bearer ${token}`
    },
    params: {
      q: `mimeType='application/vnd.google-apps.folder' and name='${folderName}' and trashed=false`,
      fields: 'files(id, name)'
    }
  })

  // If folder exists, return the folder ID
  if (searchResponse.data.files.length > 0) {
    folderId = searchResponse.data.files[0].id
  } else {
    // Step 2: If folder doesn't exist, create it
    const createResponse = await axios.post(
      'https://www.googleapis.com/drive/v3/files',
      {
        name: folderName,
        mimeType: 'application/vnd.google-apps.folder'
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    )

    folderId = createResponse.data.id
  }

  const formData = new FormData()

  const metadata = {
    name:
      file.name ||
      fileName ||
      (file.type && file.type.split('/')[1]
        ? `${Math.random()
            .toString(36)
            .substring(2, 20)}.${file.type.split('/')[1]}`
        : Math.random()
            .toString(36)
            .substring(2, 20)),
    mimeType: file.type,
    parents: folderId ? [folderId] : undefined
  }

  formData.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }))

  formData.append('file', file)

  const uploadResponse = await axios.post('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', formData, {
    onUploadProgress: setProgress
      ? progressEvent => {
          setProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total))
        }
      : undefined,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/related'
    }
  })

  // Make file public
  await axios.post(
    `https://www.googleapis.com/drive/v3/files/${uploadResponse.data.id}/permissions`,
    {
      role: 'reader',
      type: 'anyone'
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  )

  return {
    data: {
      signS3: {
        url: uploadResponse.data.mimeType.startsWith('image')
          ? `https://drive.google.com/thumbnail?id=${uploadResponse.data.id}`
          : `https://drive.google.com/file/d/${uploadResponse.data.id}/preview`
      }
    }
  }
}

const phoneLink = (phoneNumber, patient, appointment, link) => (
  <Dropdown
    menu={{
      onClick: e => e?.stopPropagation?.(),
      items: [
        {
          label: (
            <a href={`tel:${phoneNumber.replace('_', ' ')}`}>
              <Button type="text" icon={<PhoneOutlined />} />
            </a>
          ),
          key: '0'
        },
        {
          label: patient ? (
            <WhatsAppButton appointment={appointment} patient={patient} size="small" link={link} />
          ) : phoneNumber && typeof phoneNumber === 'string' && phoneNumber.replace('_', '') ? (
            <a data-testid="whatsapp-main-link" href={getWhatsAppLink(phoneNumber)} target="_blank" rel="noreferrer">
              <Button type="text" icon={<WhatsAppOutlined />} />
            </a>
          ) : (
            ''
          ),
          key: '1'
        }
      ]
    }}
    trigger={['click']}
  >
    <a
      onClick={e => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      <p className="phone-number-text">{phoneNumber.replace('_', ' ')}</p>
    </a>
  </Dropdown>
)

export const paymentsTypes = [
  'CASH',
  'CARD',
  'INSURANCE',
  'CHEQUE',
  'BALANCE',
  'VOUCHER',
  'BANK_TRANSFER',
  'MOBILE_WALLET',
  'OTHER'
]
export const operationsTypes = ['PLANNED', 'IN_PROGRESS', 'COMPLETED', 'EXISTING', 'REJECTED']
export const paymentTypesColors = {
  CASH: '#5fcb28',
  CARD: '#6495f9',
  BALANCE: '#75cbed',
  INSURANCE: '#e84ba2',
  CHEQUE: '#894ae2',
  VOUCHER: '#154498',
  BANK_TRANSFER: '#d0c226',
  MOBILE_WALLET: '#e08121',
  OTHER: '#657798'
}
export const paymentTypesWithRefundColors = {
  CASH: '#5fcb28',
  CARD: '#6495f9',
  BALANCE: '#75cbed',
  INSURANCE: '#e84ba2',
  CHEQUE: '#894ae2',
  VOUCHER: '#154498',
  BANK_TRANSFER: '#d0c226',
  MOBILE_WALLET: '#e08121',
  REFUND: '#ff3e3e',
  OTHER: '#657798'
}
export const paymentTypesWithBankColors = {
  CASH: '#5fcb28',
  BANK: '#003fc4',
  SAFE: '#c47f00',
  CARD: '#6495f9',
  BALANCE: '#75cbed',
  INSURANCE: '#e84ba2',
  CHEQUE: '#894ae2',
  VOUCHER: '#154498',
  BANK_TRANSFER: '#d0c226',
  MOBILE_WALLET: '#e08121',
  OTHER: '#657798'
}
export const invoiceColors = ['#5fcb28', '#d73939', '#657798']
export const operationsTypesColors = {
  EXISTING: '#657798',
  PLANNED: '#ee5353',
  COMPLETED: '#5dbb62',
  REJECTED: '#9b37cd',
  IN_PROGRESS: '#6495f9',
  CONDITION: '#e08121'
}

export const jobs = ['doctor', 'nurse', 'accountant', 'admin']

export const handleSearchType = (e, setTypeDataSource, types) => {
  const hasType = types.find(type => type.toLowerCase().includes(e.toLowerCase()))
  if (hasType) {
    setTypeDataSource(types)
  } else {
    setTypeDataSource([e, ...types])
  }
}

export const tablePhoneNumber = (t, key = 'phoneNumber') => ({
  className: 'tablePhoneNumber',
  title: t('common.yourPhone'),
  key,
  dataIndex: key,
  sorter: true,
  render: phoneNumber => (phoneNumber ? phoneLink(phoneNumber) : '')
})

export const tablePatientPhoneNumber = t => ({
  className: 'tablePhoneNumber',
  title: t('common.yourPhone'),
  key: 'phoneNumber',
  sorter: true,
  render: patient => (patient.phoneNumber ? phoneLink(patient.phoneNumber, patient) : '')
})

export const renderPhoneLink = (phoneNumber, patient, appointment, link) =>
  phoneNumber ? phoneLink(phoneNumber, patient, appointment, link) : ''

export const tableEmail = (t, key = 'email') => ({
  title: t('common.email'),
  key,
  dataIndex: key,
  sorter: true,
  className: 'hide-on-print',
  render: email =>
    email && email.includes('@') ? (
      <a onClick={e => e.stopPropagation()} href={`mailto:${email}`}>
        {email}
      </a>
    ) : (
      ''
    )
})

export const renderEmailLink = email =>
  email && email.includes('@') ? (
    <a onClick={e => e.stopPropagation()} href={`mailto:${email}`}>
      {email}
    </a>
  ) : (
    ''
  )

export const formatBytes = (a, b) => {
  if (a === 0) return '0 Bytes'
  const c = 1024
  const d = b || 2
  const e = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
  const f = Math.floor(Math.log(Math.abs(a)) / Math.log(c))
  return `${a > 0 ? '' : '- '}${parseFloat((Math.abs(a) / Math.pow(c, f)).toFixed(d)) + ' ' + e[f]}`
}

export const b64toBlob = imageSrc => {
  const block = imageSrc.split(';')
  const contentType = block[0].split(':')[1]
  const b64Data = block[1].split(',')[1]
  const sliceSize = 512
  const byteCharacters = atob(b64Data)
  const byteArrays = []

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize)

    const byteNumbers = new Array(slice.length)
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i)
    }

    const byteArray = new Uint8Array(byteNumbers)
    byteArrays.push(byteArray)
  }

  return new Blob(byteArrays, { type: contentType })
}

export function msToTime(s) {
  function pad(n, z) {
    z = z || 2
    return ('00' + n).slice(-z)
  }

  const ms = s % 1000
  s = (s - ms) / 1000
  const secs = s % 60
  s = (s - secs) / 60
  const mins = s % 60
  const hrs = (s - mins) / 60

  return (hrs > 99 ? hrs : pad(hrs)) + 'h : ' + pad(mins) + 'm : ' + pad(secs) + 's'
}

export const ratingDescriptions = ['terrible', 'bad', 'normal', 'good', 'wonderful']

export const getTagColorForCount = (count, redCount = 10000, orangeCount = 1000, yellowCount = 500, cyanCount = 100) => {
  if (count > redCount) return '#c90000'
  if (count > orangeCount) return 'red'
  if (count > yellowCount) return 'orange'
  if (count > cyanCount) return 'blue'
  return ''
}

export const handlePostLogout = () => {
  window.location.href = '/auth/login'
}

export const feedbackSubjects = ['giveFeedback', 'reportBug', 'suggestImprovement']

export const genders = ['male', 'female']

export const maritalStatuses = ['married', 'single', 'divorced', 'separated', 'widowed']

export const securityLogsColors = {
  'Failed Login': '#ff0000',
  'Password Change': '#ff0000',
  'Email Change': '#ff0000',
  'Password Reset Attempt': '#e8692a',
  'Disable 2FA': '#e8692a',
  'Successful Password Reset': '#e8692a',
  'Enable 2FA': '#0d72bb',
  'Successful Login': '#87d068'
}

export const deletedItemsColors = {
  PATIENTBALANCE: '#ff0000',
  INVOICE: '#ff0000',
  PAYMENT: '#ff0000',
  PRICELIST: '#ff0000',
  TREASURY: '#ff0000',
  QUOTATION: '#ff0000',
  INVENTORYUSAGE: '#e8692a',
  REMINDERINSTANCE: '#e8692a',
  TOPUP: '#e8692a',
  MONTHLYEXPENSE: '#e8692a',
  EXPENSE: '#e8692a',
  EXPENSE_PAYMENT: '#e8692a',
  INCOME: '#e8692a',
  TRANSACTION: '#e8692a',
  APPOINTMENT: '#0d72bb',
  OPERATION: '#0d72bb',
  INSURANCECOMPANY: '#0d72bb',
  PATIENTREMINDERS: '#0d72bb',
  LAB: '#0d72bb',
  SUPPLIER: '#0d72bb',
  LABORDER: '#0d72bb',
  INVENTORY: '#0d72bb',
  PATIENT: '#0d72bb',
  MESSAGE: '#2db7f5',
  PROCEDURE: '#2db7f5',
  MEDICAL: '#2db7f5',
  CLINICAL: '#2db7f5',
  CONDITION: '#2db7f5',
  FIELD: '#87d068',
  MEDICATION: '#87d068',
  FORM: '#87d068',
  FILE: '#87d068',
  XRAY: '#87d068',
  GROUP: '#87d068',
  SALARY_ADJUSTMENT: '#87d068',
  TASK: '#87d068',
  PRESCRIPTION: '#87d068',
  NOTE: '#87d068',
  ENCOUNTER: '#87d068'
}

export const sanitizeContent = details => details?.replace(/<(.|\n)*?>/g, '').trim() ?? ''

export const alphabet = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
  'P',
  'Q',
  'R',
  'S',
  'T',
  'U',
  'V',
  'W',
  'X',
  'Y',
  'Z'
]

export const patientJobs = [
  'Chiropractor',
  'Doctor',
  'Dentist',
  'Dietitian or Nutritionist',
  'Optometrist',
  'Pharmacist',
  'Physician',
  'Physician Assistant',
  'Podiatrist',
  'Registered Nurse',
  'Therapist',
  'Veterinarian',
  'Health Technologist or Technician',
  'Other Healthcare Practitioners and Technical Occupation',
  'Nursing, Psychiatric, or Home Health Aide',
  'Occupational and Physical Therapist Assistant or Aide',
  'Other Healthcare Support Occupation',
  'Chief Executive',
  'General and Operations Manager',
  'Advertising, Marketing, Promotions, Public Relations, and Sales Manager',
  'Operations Specialties Manager (e.g., IT or HR Manager)',
  'Construction Manager',
  'Engineering Manager',
  'Accountant, Auditor',
  'Business Operations or Financial Specialist',
  'Business Owner',
  'Other Business, Executive, Management, Financial Occupation',
  'Architect, Surveyor, or Cartographer',
  'Engineer',
  'Other Architecture and Engineering Occupation',
  'Postsecondary Teacher (e.g., College Professor)',
  'Primary, Secondary, or Special Education School Teacher',
  'Other Teacher or Instructor',
  'Other Education, Training, and Library Occupation',
  'Arts, Design, Entertainment, Sports, and Media Occupations',
  'Computer Specialist, Mathematical Science',
  'Counselor, Social Worker, or Other Community and Social Service Specialist',
  'Lawyer, Judge',
  'Life Scientist (e.g., Animal, Food, Soil, or Biological Scientist, Zoologist)',
  'Physical Scientist (e.g., Astronomer, Physicist, Chemist, Hydrologist)',
  'Religious Worker (e.g., Clergy, Director of Religious Activities or Education)',
  'Social Scientist and Related Worker',
  'Other Professional Occupation',
  'Supervisor of Administrative Support Workers',
  'Financial Clerk',
  'Secretary or Administrative Assistant',
  'Material Recording, Scheduling, and Dispatching Worker',
  'Other Office and Administrative Support Occupation',
  'Protective Service (e.g., Fire Fighting, Police Officer, Correctional Officer)',
  'Chef or Head Cook',
  'Cook or Food Preparation Worker',
  'Food and Beverage Serving Worker (e.g., Bartender, Waiter, Waitress)',
  'Building and Grounds Cleaning and Maintenance',
  'Personal Care and Service (e.g., Hairdresser, Flight Attendant, Concierge)',
  'Sales Supervisor, Retail Sales',
  'Retail Sales Worker',
  'Insurance Sales Agent',
  'Sales Representative',
  'Real Estate Sales Agent',
  'Other Services Occupation',
  'Construction and Extraction (e.g., Construction Laborer, Electrician)',
  'Farming, Fishing, and Forestry',
  'Installation, Maintenance, and Repair',
  'Production Occupations',
  'Other Agriculture, Maintenance, Repair, and Skilled Crafts Occupation',
  'Aircraft Pilot or Flight Engineer',
  'Motor Vehicle Operator (e.g., Ambulance, Bus, Taxi, or Truck Driver)',
  'Other Transportation Occupation',
  'Military',
  'Homemaker',
  'Other Occupation',
  "Don't Know",
  'Not Applicable'
]

export const requiredPatientFields = {
  phoneNumber2: 'common.phoneNumber2',
  email: 'common.email',
  patientId: 'app.patientId',
  birthDate: 'common.birthDate',
  middleNames: 'calendar.middleNames',
  age: 'common.age',
  patientTitle: 'common.nameTitle',
  gender: 'calendar.gender',
  address: 'common.address',
  assignedPractitioner: 'calendar.assignedPractitioner',
  priceList: 'patients.priceListG',
  job: 'common.job',
  tax: 'settings.account.tax',
  patientDetails: 'calendar.patientDetails',
  insuranceDetails: 'calendar.insuranceDetails',
  referralDetails: 'patients.referralDetails',
  referralSource: 'patients.referralSource',
  marital: 'patients.marital',
  emergencyContact: 'patients.emergencyContact',
  insuranceNumber: 'app.insuranceNumber',
  insuranceGroup: 'app.insuranceGroup',
  insuranceCRT: 'app.insuranceCRT',
  insuranceEndDate: 'app.insuranceEndDate',
  insuranceStartMonth: 'app.insuranceStartMonth',
  maxInsuranceLimit: 'settings.insurance.maxLimit',
  tags: 'app.patientTags',
  nationality: 'dashboardHome.patientsNationality',
  nationalId: 'app.nationalId'
}

export const visiblePatientFields = {
  phoneNumber2: 'common.phoneNumber2',
  email: 'common.email',
  birthDate: 'common.birthDate',
  patientTitle: 'common.nameTitle',
  gender: 'calendar.gender',
  address: 'common.address',
  job: 'common.job',
  patientDetails: 'calendar.patientDetails',
  referralDetails: 'patients.referralDetails',
  referralSource: 'patients.referralSource',
  marital: 'patients.marital',
  emergencyContact: 'patients.emergencyContact',
  insuranceNumber: 'app.insuranceNumber',
  insuranceGroup: 'app.insuranceGroup',
  insuranceCRT: 'app.insuranceCRT',
  insuranceEndDate: 'app.insuranceEndDate',
  insuranceStartMonth: 'app.insuranceStartMonth',
  nationality: 'dashboardHome.patientsNationality',
  nationalId: 'app.nationalId'
}

export const requiredLeadFields = {
  phoneNumber2: 'common.phoneNumber2',
  email: 'common.email',
  middleNames: 'calendar.middleNames',
  address: 'common.address',
  assignedTo: 'calendar.assignedTo',
  due: 'app.dueDate',
  dealSize: 'app.dealSize',
  score: 'app.score',
  patientDetails: 'common.details',
  referralSource: 'patients.referralSource',
  tags: 'app.patientTags',
  nationality: 'dashboardHome.patientsNationality',
  nationalId: 'app.nationalId'
}

export const TrainingVideos = {
  ACCOUNT: 'https://youtu.be/2WPeVjhRheg',
  BRANCHES: 'https://youtu.be/e7QJxq23sFA',
  USERS: 'https://youtu.be/XJzoyKqJZt0',
  PERMISSIONS: 'https://youtu.be/rsIdoeQQqxQ',
  WHATSAPP: 'https://youtu.be/jLFUCdUdqF4',
  MONTHLY_EXPENSES: 'https://youtu.be/4rRT2eb91Zs',
  PROCEDURES: 'https://youtu.be/q1qCZkEuODo',
  CONDITIONS: 'https://youtu.be/hRwgyV2vytk',
  MEDICAL: 'https://youtu.be/oNdaoAh-8Bs',
  CLINICAL: 'https://youtu.be/woJfYBrtWeo',
  NOTES: 'https://youtu.be/C14f_c-JrtY',
  MEDICATIONS: 'https://youtu.be/FoeL2hjls6M',
  REMINDERS: 'https://youtu.be/LvZFDF9D3DE',
  FORMS: 'https://youtu.be/h9nOe5xl2Xk'
}

export const expenseMainTypes = [
  { value: 'OPERATING_EXPENSES', color: 'cyan' },
  { value: 'COST_OF_REVENUE', color: 'blue' },
  { value: 'TAX', color: 'purple' },
  { value: 'OTHER', color: 'red' }
]

export const SMS_PROVIDERS = [
  'SMS_MISR',
  'SMS_EG',
  'TWILIO',
  'MSEGAT',
  'LIBYA_SMS',
  'COUNTRY_SMS',
  'WEB_SMS',
  'ONFON_SMS_KENYA',
  'GATEWAY_SA',
  'TUNISIE_SMS',
  'BAREED_SMS',
  'OURSMS_SA',
  'NGT_JO',
  'HUBTEL_GH',
  'VF_EGYPT',
  'BROAD_JO',
  'TAWASOL_SMS_SA',
  'ARPU_EG',
  'MRAM_BD'
]

export const renderTextWithLink = (text, userName) => {
  if (!text) return null
  const isUrl = word => {
    const expression = /[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&/=]*)/gi
    const regex = new RegExp(expression)
    return word.match(regex)
  }

  const addMarkup = word => {
    return isUrl(word)
      ? `<a href="${!word.startsWith('http') ? 'https://' : ''}${word}" rel="noopener noreferrer" target="_blank">${word}</a>`
      : word
  }

  const words = text.split(' ')
  const formatedWords = words.map(w => addMarkup(w))
  const html = DOMPurify.sanitize(
    userName
      ? formatedWords.join(' ').replace(`@${userName}`, `<span style="color: #b25018;font-weight: bold">@${userName}</span>`)
      : formatedWords.join(' '),
    { ADD_ATTR: ['target'] }
  )
  return <span dangerouslySetInnerHTML={{ __html: html }} />
}

export const onlinePaymentsStatuses = [
  { name: 'common.created', value: 'CREATED', color: 'gold' },
  { name: 'app.pending', value: 'PENDING', color: 'orange' },
  { name: 'app.success', value: 'SUCCESS', color: 'green' },
  { name: 'support.failed', value: 'FAILED', color: 'red' },
  { name: 'app.refunded', value: 'REFUND', color: 'magenta' }
]

export const paymentProviders = [
  {
    name: 'Geidea',
    value: 'GEIDEA',
    fields: ['paymentUsername', 'paymentPassword'],
    resend: true,
    callback: 'https://api.dentolize.com/geidea/onlinePayment'
  },
  {
    name: 'Paymob',
    value: 'PAYMOB',
    fields: ['paymentIntegration', 'paymentPassword'],
    resend: false,
    callback: 'https://api.dentolize.com/paymob/onlinePayment'
  },
  {
    name: 'My Fatoorah',
    value: 'MYFATOORA',
    fields: ['paymentPassword'],
    resend: false,
    callback: 'https://api.dentolize.com/myFatoorah/onlinePayment'
  },
  {
    name: 'Stripe',
    value: 'STRIPE',
    fields: ['paymentPassword'],
    resend: false,
    callback: 'https://api.dentolize.com/stripe/onlinePayment'
  },
  {
    name: 'Fawry',
    value: 'FAWRY',
    fields: ['paymentUsername', 'paymentPassword', 'paymentMethod'],
    resend: false,
    callback: 'https://api.dentolize.com/fawry/onlinePayment'
  },
  {
    name: 'Vast Pay',
    value: 'VAST',
    fields: ['paymentClient', 'paymentPassword', 'paymentTable'],
    resend: false,
    callback: 'https://api.dentolize.com/vastPay/onlinePayment'
  }
]

export const getAgeWithMonthAndDays = (date, birthDay) => {
  const birthDate = moment(date)
  const today = moment()
  const years = today.diff(birthDate, 'year')

  if (!birthDay) return `${years}y`

  const months = today.diff(moment(birthDate).add(years, 'year'), 'month')

  if (years < 5) {
    const days = today.diff(moment(birthDate).add(years, 'year').add(months, 'month'), 'day')
    return `${years}y ${months}m ${days}d`
  }

  return `${years}y ${months}m`
}
